import { ReactNode } from 'react';
import { X } from 'lucide-react';

type Tone = 'neutral' | 'info' | 'warm';

const TONE: Record<Tone, string> = {
  // Admin `.note`.
  neutral: 'bg-note text-muted-2',
  // Auth `.note`.
  info: 'bg-mist text-muted-2',
  // Paper `.note`: warm fill with a border.
  warm: 'border border-accent-line bg-accent-bg text-warm-ink',
};

interface Props {
  children: ReactNode;
  tone?: Tone;
  /** Leading icon (16-18px lucide icon). */
  icon?: ReactNode;
  /** Adds a 36px dismiss button on the right. */
  onDismiss?: () => void;
  dismissLabel?: string;
  /** `role="status"` for messages that appear after an action. */
  role?: 'status' | 'note';
  className?: string;
}

/** Design `.note` callout: a 14px-radius tinted box with 13px text and an optional icon. */
export default function Callout({ children, tone = 'neutral', icon, onDismiss, dismissLabel = 'Dismiss', role = 'note', className = '' }: Props) {
  return (
    <div role={role} className={`flex items-start gap-2.5 rounded-[14px] px-3.5 py-3 text-[13px] ${TONE[tone]} ${className}`}>
      {icon && <span aria-hidden="true" className="mt-px flex-none">{icon}</span>}
      <div className="min-w-0 flex-1">{children}</div>
      {onDismiss && (
        <button type="button" aria-label={dismissLabel} onClick={onDismiss}
          className="-my-1.5 -mr-1.5 grid h-9 w-9 flex-none place-items-center rounded-[10px] bg-transparent p-0 text-current hover:bg-navy/[.07] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
          <X size={16} strokeWidth={1.8} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
